/**
 * 控制台路由（总纲 §6）。页面 → 权限点见 ./pages.ts；旧路径 → 新路径见 ./redirects.ts。
 * 路由级分包保持（spec 08 §8）。
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router';
import { AppShell } from '@/components/shell/AppShell';
import { Guard, Loading } from '@/components/shell/Guard';
import { RESOURCE_TABS } from '@/components/shell/navItems';
import { SubNavOutlet } from '@/components/shell/SubNav';
import { pageCap } from './pages';
import { LEGACY_EMPLOYEE_TABS, LEGACY_PATHS, resolveLegacyPath } from './redirects';

const Marketing = lazy(() => import('@/pages/marketing'));
const Login = lazy(() => import('@/pages/login'));
const Register = lazy(() => import('@/pages/register'));
const Legal = lazy(() => import('@/pages/legal'));

const Today = lazy(() => import('@/pages/today'));
const Employees = lazy(() => import('@/pages/employees'));
const Hire = lazy(() => import('@/pages/employees/hire'));
const EmployeeHome = lazy(() => import('@/pages/employees/home'));
const Customers = lazy(() => import('@/pages/customers'));
const CustomerLeads = lazy(() => import('@/pages/customers/leads'));
const CustomerCalls = lazy(() => import('@/pages/customers/calls'));
const CustomerIntents = lazy(() => import('@/pages/customers/intents'));
const Work = lazy(() => import('@/pages/work'));
const Billing = lazy(() => import('@/pages/billing'));
const BillingMonth = lazy(() => import('@/pages/billing/month'));
const BillingSeats = lazy(() => import('@/pages/billing/seats'));
const BillingBills = lazy(() => import('@/pages/billing/bills'));
const BillingInvoices = lazy(() => import('@/pages/billing/invoices'));
const Settings = lazy(() => import('@/pages/settings'));
const SettingsCompany = lazy(() => import('@/pages/settings/company'));
const SettingsMembers = lazy(() => import('@/pages/settings/members'));
const SettingsLines = lazy(() => import('@/pages/settings/lines'));
const SettingsSms = lazy(() => import('@/pages/settings/sms'));
const SettingsWechat = lazy(() => import('@/pages/settings/wechat'));
const SettingsDeveloper = lazy(() => import('@/pages/settings/developer'));
const SettingsExports = lazy(() => import('@/pages/settings/exports'));
const SettingsLogs = lazy(() => import('@/pages/settings/logs'));
const Inbox = lazy(() => import('@/pages/inbox'));
const Profile = lazy(() => import('@/pages/account/profile'));
const Help = lazy(() => import('@/pages/help'));
const NotFound = lazy(() => import('@/pages/not-found'));

function page(path: string, node: ReactNode) {
  const cap = pageCap(path);
  return <Guard {...(cap ? { cap } : {})}>{node}</Guard>;
}

function pub(node: ReactNode) {
  return <Suspense fallback={<Loading />}>{node}</Suspense>;
}

/** 整页级旧路径：查询串原样带过去。 */
function LegacyRedirect() {
  const { pathname, search } = useLocation();
  const to = resolveLegacyPath(pathname, search);
  return <Navigate to={to ?? '/today'} replace />;
}

/** 员工页旧 tab：落到面 + 锚点；不认识的 tab 交给 404。 */
function LegacyEmployeeTab() {
  const { id = '', tab = '' } = useParams();
  const { search } = useLocation();
  const to = resolveLegacyPath(`/employees/${id}/${tab}`, search);
  if (!to) return pub(<NotFound />);
  return <Navigate to={to} replace />;
}

const legacyRoutes = Object.keys(LEGACY_PATHS).map((p) => ({
  path: p.slice(1),
  element: <LegacyRedirect />,
}));

const legacyTabRoutes = Object.keys(LEGACY_EMPLOYEE_TABS).map((t) => ({
  path: `employees/:id/${t}`,
  element: <LegacyEmployeeTab />,
}));

export const router = createBrowserRouter([
  { path: '/', element: pub(<Marketing />) },
  { path: '/updates', element: pub(<Marketing />) },
  { path: '/login', element: pub(<Login />) },
  { path: '/register', element: pub(<Register />) },
  // 隐私政策 / 用户协议：匿名可读，文本来自运营后台的站点信息（没配走平台默认）
  { path: '/privacy', element: pub(<Legal kind="privacy" />) },
  { path: '/terms', element: pub(<Legal kind="terms" />) },
  {
    element: <Guard>{<AppShell />}</Guard>,
    children: [
      { path: 'today', element: page('/today', <Today />) },

      { path: 'employees', element: page('/employees', <Employees />) },
      { path: 'employees/hire', element: page('/employees/hire', <Hire />) },
      // 三面走真路由，链接可分享、刷新不丢位置；旧 tab 先于 :face 匹配。
      // 路由只守容器的 cap，哪几面可见由容器按 pages.ts 里各面的 cap 收窄。
      ...legacyTabRoutes,
      { path: 'employees/:id', element: page('/employees/:id', <EmployeeHome />) },
      { path: 'employees/:id/:face', element: page('/employees/:id', <EmployeeHome />) },

      {
        path: 'customers',
        element: page('/customers', <Customers />),
        children: [
          { path: 'leads', element: page('/customers/leads', <CustomerLeads />) },
          { path: 'calls', element: page('/customers/calls', <CustomerCalls />) },
          { path: 'intents', element: page('/customers/intents', <CustomerIntents />) },
        ],
      },
      { path: 'work', element: page('/work', <Work />) },

      {
        path: 'billing',
        element: page('/billing', <Billing />),
        children: [
          { index: true, element: page('/billing', <BillingMonth />) },
          { path: 'seats', element: page('/billing/seats', <BillingSeats />) },
          { path: 'bills', element: page('/billing/bills', <BillingBills />) },
          { path: 'invoices', element: page('/billing/invoices', <BillingInvoices />) },
        ],
      },

      {
        path: 'settings',
        element: page('/settings', <Settings />),
        children: [
          { path: 'company', element: page('/settings/company', <SettingsCompany />) },
          { path: 'members', element: page('/settings/members', <SettingsMembers />) },
          {
            path: 'resources',
            element: page(
              '/settings/resources/lines',
              <SubNavOutlet items={RESOURCE_TABS} label="电话线路 / 短信 / 加微" />,
            ),
            children: [
              { index: true, element: <Navigate to="lines" replace /> },
              { path: 'lines', element: page('/settings/resources/lines', <SettingsLines />) },
              { path: 'sms', element: page('/settings/resources/sms', <SettingsSms />) },
              {
                path: 'wechat',
                element: page('/settings/resources/wechat', <SettingsWechat />),
              },
            ],
          },
          { path: 'developer', element: page('/settings/developer', <SettingsDeveloper />) },
          { path: 'exports', element: page('/settings/exports', <SettingsExports />) },
          { path: 'logs', element: page('/settings/logs', <SettingsLogs />) },
        ],
      },

      { path: 'inbox', element: page('/inbox', <Inbox />) },
      { path: 'account/profile', element: page('/account/profile', <Profile />) },
      { path: 'help', element: page('/help', <Help />) },

      ...legacyRoutes,
      { path: '*', element: page('*', <NotFound />) },
    ],
  },
]);
